// ============================================
// CHÁMOMILE - Loyalty Service (Online Ordering App)
// Shared points system with POS app via Firestore `members` collection
// ============================================

import { doc, getDoc, setDoc, updateDoc, collection, getDocs, serverTimestamp, increment } from 'firebase/firestore';
import { db } from '../config/firebase';

// ============================================
// TYPES
// ============================================

interface Reward {
    id: string;
    points: number;
    label: string;
    value: number;
    type: string;
}

interface Tier {
    name: string;
    icon: string;
    minPoints: number;
    color: string;
}

interface MemberData {
    id: string;
    name?: string;
    email?: string;
    phone?: string;
    authUid?: string;
    loyaltyPoints?: number;
    totalSpent?: number;
    totalOrders?: number;
    registeredVia?: string;
    [key: string]: any;
}

// ============================================
// CONSTANTS
// ============================================

// Points: 1 point per RM spent (buy 10 free 1 basis)
const POINTS_PER_RM = 1;

// ============================================
// REWARDS UI KILL SWITCH
// ============================================
// The old points/tier/rewards programme is being redesigned (Aug 2026).
// While `false`, every customer-facing rewards surface is hidden:
// points balance, tier badges, reward ladder, redemption prompts and T&Cs.
//
// IMPORTANT: this hides the UI ONLY. Points continue to accrue normally in
// Firestore and every existing member balance is preserved untouched, so the
// new programme can honour them. Flip back to `true` to restore the old UI.
export const REWARDS_UI_ENABLED = false;

// Reward tiers (points needed → reward) — defaults, overwritten by Firebase settings
export let REWARDS: Reward[] = [
    { id: 'rm1off', points: 33, label: 'RM 1 Off', value: 1, type: 'discount' },
    { id: 'rm5off', points: 100, label: 'RM 5 Off', value: 5, type: 'discount' },
    { id: 'freebottle', points: 130, label: 'Free Bottle (Signature/Viral)', value: 13, type: 'freeBottle' },
];

export const setGlobalRewards = (rewards: Reward[]) => {
    if (rewards && rewards.length > 0) {
        REWARDS = rewards;
    }
};

// Membership tiers — aspirational hierarchy
export const TIERS: Tier[] = [
    { name: 'Tea Leaf', icon: '🍃', minPoints: 0, color: '#1d4ed8' },
    { name: 'Blossom', icon: '🌸', minPoints: 100, color: '#059669' },
    { name: 'Royal Blend', icon: '👑', minPoints: 500, color: '#d97706' },
    { name: 'Grand Cru', icon: '💎', minPoints: 1000, color: '#7c3aed' },
];

// ============================================
// TIER FUNCTIONS
// ============================================

/**
 * Get the membership tier for a given points balance
 */
export const getMemberTier = (points: number): Tier => {
    const p = points || 0;
    if (p >= 1000) return TIERS[3]; // Grand Cru
    if (p >= 500) return TIERS[2]; // Royal Blend
    if (p >= 100) return TIERS[1]; // Blossom
    return TIERS[0];                // Tea Leaf
};

/**
 * Get the next tier and points needed to reach it
 */
export const getNextTier = (points: number): { tier: Tier; pointsNeeded: number } | null => {
    const p = points || 0;
    if (p >= 1000) return null; // Already at max tier
    if (p >= 500) return { tier: TIERS[3], pointsNeeded: 1000 - p };
    if (p >= 100) return { tier: TIERS[2], pointsNeeded: 500 - p };
    return { tier: TIERS[1], pointsNeeded: 100 - p };
};

// ============================================
// REWARDS FUNCTIONS
// ============================================

/**
 * Get available rewards based on current points balance
 */
export const getAvailableRewards = (points: number): Reward[] => {
    return REWARDS.filter(r => points >= r.points);
};

/**
 * Get the next reward to unlock
 */
export const getNextReward = (points: number): (Reward & { pointsNeeded: number }) | null => {
    const next = REWARDS.find(r => points < r.points);
    if (!next) return null;
    return { ...next, pointsNeeded: next.points - points };
};

// ============================================
// MEMBER LOOKUP FUNCTIONS
// ============================================

/**
 * Get member data by phone number (the doc ID in `members` collection)
 */
export const getMemberByPhone = async (phone: string): Promise<MemberData | null> => {
    try {
        const cleanPhone = phone.replace(/\D/g, '');
        if (!cleanPhone || cleanPhone.length < 8) return null;

        const memberRef = doc(db, 'members', cleanPhone);
        const memberSnap = await getDoc(memberRef);

        if (memberSnap.exists()) {
            return { ...memberSnap.data(), id: memberSnap.id } as MemberData;
        }
        return null;
    } catch (error) {
        console.error('[LoyaltyService] Error getting member by phone:', error);
        return null;
    }
};

/**
 * Get member data by email
 */
export const getMemberByEmail = async (email: string): Promise<MemberData | null> => {
    try {
        if (!email) return null;

        const membersSnapshot = await getDocs(collection(db, 'members'));
        for (const docSnap of membersSnapshot.docs) {
            const data = docSnap.data();
            if (data.email && data.email.toLowerCase() === email.toLowerCase()) {
                return { ...data, id: docSnap.id } as MemberData;
            }
        }
        return null;
    } catch (error) {
        console.error('[LoyaltyService] Error getting member by email:', error);
        return null;
    }
};

/**
 * Get member data by auth UID
 */
export const getMemberByAuthUid = async (authUid: string): Promise<MemberData | null> => {
    try {
        if (!authUid) return null;

        const membersSnapshot = await getDocs(collection(db, 'members'));
        for (const docSnap of membersSnapshot.docs) {
            const data = docSnap.data();
            if (data.authUid === authUid) {
                return { ...data, id: docSnap.id } as MemberData;
            }
        }
        return null;
    } catch (error) {
        console.error('[LoyaltyService] Error getting member by authUid:', error);
        return null;
    }
};

// ============================================
// POINTS EARNING
// ============================================

/**
 * Award points to a member after a completed order.
 * Matches the POS logic in useFirebase.js updateMemberPointsAfterOrder
 */
export const earnPoints = async (memberId: string, orderTotal: number, orderId: string, source: string = 'online') => {
    try {
        if (!memberId || !orderTotal) {
            console.warn('[LoyaltyService] Missing memberId or orderTotal for earnPoints');
            return { success: false };
        }

        // All orders from ONLINE shall not calculate points
        const isOnline = source && source.toLowerCase() === 'online';
        const pointsEarned = isOnline ? 0 : Math.floor(orderTotal * POINTS_PER_RM);
        const memberRef = doc(db, 'members', memberId);
        const memberSnap = await getDoc(memberRef);

        if (!memberSnap.exists()) {
            console.warn(`[LoyaltyService] Member ${memberId} not found`);
            return { success: false, message: 'Member not found' };
        }

        const currentData = memberSnap.data();
        const currentPoints = currentData.loyaltyPoints || 0;
        const newBalance = currentPoints + pointsEarned;

        await updateDoc(memberRef, {
            loyaltyPoints: newBalance,
            totalSpent: increment(orderTotal),
            totalOrders: increment(1),
            lastOrderDate: serverTimestamp(),
            lastUpdated: serverTimestamp(),
            pointsHistory: {
                lastEarned: pointsEarned,
                lastRedeemed: 0,
                lastUpdatedAt: serverTimestamp(),
                previousBalance: currentPoints,
                newBalance: newBalance,
                source: source,
                orderId: orderId || ''
            }
        });

        console.log(`[LoyaltyService] +${pointsEarned} pts → ${newBalance} pts for member ${memberId}`);

        return {
            success: true,
            pointsEarned,
            newBalance,
            previousBalance: currentPoints
        };
    } catch (error: any) {
        console.error('[LoyaltyService] Error earning points:', error);
        return { success: false, message: error.message };
    }
};

// ============================================
// POINTS REDEMPTION
// ============================================

/**
 * Redeem points for a reward
 */
export const redeemPoints = async (memberId: string, rewardId: string) => {
    try {
        const reward = REWARDS.find(r => r.id === rewardId);
        if (!reward) return { success: false, message: 'Invalid reward' };

        const memberRef = doc(db, 'members', memberId);
        const memberSnap = await getDoc(memberRef);

        if (!memberSnap.exists()) return { success: false, message: 'Member not found' };

        const data = memberSnap.data();
        const currentPoints = data.loyaltyPoints || 0;

        if (currentPoints < reward.points) {
            return { success: false, message: `Not enough points. Need ${reward.points}, have ${currentPoints}.` };
        }

        const newBalance = currentPoints - reward.points;

        await updateDoc(memberRef, {
            loyaltyPoints: newBalance,
            lastUpdated: serverTimestamp(),
            pointsHistory: {
                lastEarned: 0,
                lastRedeemed: reward.points,
                lastUpdatedAt: serverTimestamp(),
                previousBalance: currentPoints,
                newBalance: newBalance,
                rewardRedeemed: reward.label
            }
        });

        console.log(`[LoyaltyService] Redeemed ${reward.label} (${reward.points} pts) → ${newBalance} pts remaining`);

        return {
            success: true,
            reward,
            newBalance,
            previousBalance: currentPoints
        };
    } catch (error: any) {
        console.error('[LoyaltyService] Error redeeming points:', error);
        return { success: false, message: error.message };
    }
};

// ============================================
// ONLINE REGISTRATION → MEMBER LINKING
// ============================================

/**
 * Link an online auth user to the members collection.
 * If a member with the same phone exists, link authUid to it.
 * If not, create a new member entry using phone as doc ID.
 */
export const linkAuthToMember = async (user: any, phone: string, displayName: string): Promise<MemberData | null> => {
    try {
        const cleanPhone = phone ? phone.replace(/\D/g, '') : '';

        // Strategy 1: Find existing member by phone
        if (cleanPhone && cleanPhone.length >= 8) {
            const existingByPhone = await getMemberByPhone(cleanPhone);
            if (existingByPhone) {
                // Link authUid to existing member
                const memberRef = doc(db, 'members', existingByPhone.id);
                await updateDoc(memberRef, {
                    authUid: user.uid,
                    email: user.email || existingByPhone.email || '',
                    lastLogin: serverTimestamp(),
                    lastUpdated: serverTimestamp()
                });
                console.log(`[LoyaltyService] Linked auth ${user.uid} to existing member ${existingByPhone.id}`);
                return { ...existingByPhone, authUid: user.uid };
            }
        }

        // Strategy 2: Find existing member by email
        if (user.email) {
            const existingByEmail = await getMemberByEmail(user.email);
            if (existingByEmail) {
                const memberRef = doc(db, 'members', existingByEmail.id);
                await updateDoc(memberRef, {
                    authUid: user.uid,
                    lastLogin: serverTimestamp(),
                    lastUpdated: serverTimestamp()
                });
                console.log(`[LoyaltyService] Linked auth ${user.uid} to member by email ${existingByEmail.id}`);
                return { ...existingByEmail, authUid: user.uid };
            }
        }

        // Strategy 3: Find by existing authUid
        const existingByAuth = await getMemberByAuthUid(user.uid);
        if (existingByAuth) {
            const memberRef = doc(db, 'members', existingByAuth.id);
            await updateDoc(memberRef, { lastLogin: serverTimestamp() });
            return existingByAuth;
        }

        // Strategy 4: Create new member (phone as ID if available, otherwise authUid)
        const memberId = cleanPhone && cleanPhone.length >= 8 ? cleanPhone : user.uid;
        const memberRef = doc(db, 'members', memberId);
        const newMemberData: MemberData = {
            id: memberId,
            name: displayName || user.displayName || '',
            email: user.email || '',
            phone: cleanPhone || '',
            authUid: user.uid,
            loyaltyPoints: 0,
            totalSpent: 0,
            totalOrders: 0,
            registeredVia: 'online',
        };

        await setDoc(memberRef, {
            ...newMemberData,
            createdAt: serverTimestamp(),
            lastLogin: serverTimestamp(),
            lastUpdated: serverTimestamp()
        });
        console.log(`[LoyaltyService] Created new member ${memberId} via online registration`);

        return newMemberData;
    } catch (error) {
        console.error('[LoyaltyService] Error linking auth to member:', error);
        return null;
    }
};

export default {
    getMemberTier,
    getNextTier,
    getAvailableRewards,
    getNextReward,
    getMemberByPhone,
    getMemberByEmail,
    getMemberByAuthUid,
    earnPoints,
    redeemPoints,
    linkAuthToMember,
    REWARDS,
    setGlobalRewards,
    TIERS,
    REWARDS_UI_ENABLED
};
